import React, { useEffect, useState } from "react";
import { TbArrowLeft, TbArrowRight, TbInfoCircleFilled } from "react-icons/tb";
import {
  Navigate,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import styled from "styled-components";
import Button from "../../components/Button";
import StepProgress from "../../components/StepProgress";
import { Body, Footnote, Subhead, Title1 } from "../../styles/typography";
import { EVENTS, track } from "../../util/analytics";
import {
  ADD_ON_OPTIONS,
  BACK_COVER_STYLE_OPTIONS,
  COLOR_SWATCH_IMAGE_URL,
  COVER_COLOR_PALETTE,
  FRONT_COVER_STYLE_OPTIONS,
  getOptionImage,
  getOptionLabel,
  PHOTO_LAYOUT_OPTIONS,
} from "./optionsConfig";
import ColorPickerStep from "./steps/ColorPickerStep";
import OptionGridStep from "./steps/OptionGridStep";
import TextFieldStep from "./steps/TextFieldStep";
import { countWords } from "./wordCount";

const PageContainer = styled.div`
  margin: 0 auto;
  margin-top: 90px;
  padding: 24px;
  background: rgb(${(p) => p.theme.colors.white});
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Description = styled(Body)`
  color: rgb(${(p) => p.theme.colors.neutral400});
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;

  button {
    width: fit-content;
  }
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgb(${(p) => p.theme.colors.error600});
`;

export const RequirementTag = styled(Footnote)`
  color: rgb(${(p) => p.theme.colors.neutral300});
`;

export const STEPS = [
  {
    key: "title",
    slug: "title",
    title: "What would you like to title your book?",
    description:
      'This will appear on your cover and first page (known as the Title Page) inside your book. Examples: "The Wonderful Memories of My Grandmother", "My Life Story", etc.',
    required: true,
    component: TextFieldStep,
    props: {
      label: "Book title",
      placeholder: "e.g. The wonderful memories of my grandmother",
      maxWords: 10,
    },
    isComplete: (d) => Boolean(d.title?.trim()) && countWords(d.title) <= 10,
    getErrorMessage: (d) =>
      countWords(d.title ?? "") > 10
        ? "Please keep this to 10 words or fewer."
        : "Please complete this required field to continue.",
    getSummary: (d) => d.title?.trim() || null,
  },
  {
    key: "subtitle",
    slug: "subtitle",
    title: "Would you like a subtitle?",
    description:
      "This will appear below your title on the cover and first page inside your book. Examples: A collection of memories about Grandma, Written by Dad, etc.",
    required: false,
    component: TextFieldStep,
    props: {
      label: "Subtitle",
      placeholder: "e.g. A collection of memories about...",
      maxWords: 20,
    },
    isComplete: (d) => countWords(d.subtitle ?? "") <= 20,
    getErrorMessage: () => "Please keep this to 20 words or fewer.",
    getSummary: (d) => d.subtitle?.trim() || null,
  },
  {
    key: "dedication",
    slug: "dedication",
    title: "Please write your dedication below",
    description:
      "This will appear on a new page after your Title Page inside your book.",
    required: false,
    component: TextFieldStep,
    props: {
      label: "Dedication",
      placeholder: "Write your dedication...",
      multiline: true,
      maxWords: 150,
    },
    isComplete: (d) => countWords(d.dedication ?? "") <= 150,
    getErrorMessage: () => "Please keep this to 150 words or fewer.",
    getSummary: (d) => d.dedication?.trim() || null,
  },
  {
    key: "photoLayout",
    slug: "photo-layout",
    title: "How would you like your photos presented per chapter?",
    required: true,
    component: OptionGridStep,
    props: { options: PHOTO_LAYOUT_OPTIONS },
    isComplete: (d) => Boolean(d.photoLayout),
    getErrorMessage: () => "Please select an option to continue.",
    getSummary: (d) => getOptionLabel(PHOTO_LAYOUT_OPTIONS, d.photoLayout),
    getImages: (d) =>
      [getOptionImage(PHOTO_LAYOUT_OPTIONS, d.photoLayout)].filter(Boolean),
  },
  {
    key: "addOns",
    slug: "add-ons",
    title: "Are you interested in add-ons?",
    description:
      "We will email you with more information and provide you with an exact quote based on your request. If you would like to proceed, we will send you a separate payment link. Choose as many as you like.",
    required: true,
    component: OptionGridStep,
    props: { options: ADD_ON_OPTIONS, multiSelect: true },
    setValue: (d, next) => {
      const prevHasNone = d.addOns?.includes("none");
      const nextHasNone = next.includes("none");
      if (nextHasNone && !prevHasNone) {
        return { ...d, addOns: ["none"] };
      }
      if (nextHasNone && next.length > 1) {
        return { ...d, addOns: next.filter((id) => id !== "none") };
      }
      return { ...d, addOns: next };
    },
    isComplete: (d) => Boolean(d.addOns?.length),
    getErrorMessage: () => "Please select an option to continue.",
    getSummary: (d) =>
      (d.addOns ?? [])
        .map((id) => getOptionLabel(ADD_ON_OPTIONS, id))
        .filter(Boolean)
        .join(", ") || null,
    getImages: (d) =>
      (d.addOns ?? [])
        .map((id) => getOptionImage(ADD_ON_OPTIONS, id))
        .filter(Boolean),
  },
  {
    key: "frontCoverStyle",
    slug: "front-cover-style",
    title: "Front cover style",
    description:
      "For any choice using your own photo(s): Our designer will email you separately to ask for the photo file.",
    required: true,
    component: OptionGridStep,
    props: { options: FRONT_COVER_STYLE_OPTIONS },
    isComplete: (d) => Boolean(d.frontCoverStyle),
    getErrorMessage: () => "Please select an option to continue.",
    getSummary: (d) =>
      getOptionLabel(FRONT_COVER_STYLE_OPTIONS, d.frontCoverStyle),
    getImages: (d) =>
      [getOptionImage(FRONT_COVER_STYLE_OPTIONS, d.frontCoverStyle)].filter(
        Boolean,
      ),
  },
  {
    key: "frontCoverColor",
    slug: "front-cover-color",
    title: "Select a color for your front cover",
    required: true,
    component: ColorPickerStep,
    props: {
      options: COVER_COLOR_PALETTE,
      swatchImageUrl: COLOR_SWATCH_IMAGE_URL,
    },
    isComplete: (d) => Boolean(d.frontCoverColor),
    getErrorMessage: () => "Please select a color to continue.",
    getSummary: (d) => getOptionLabel(COVER_COLOR_PALETTE, d.frontCoverColor),
    getImages: (d) => (d.frontCoverColor ? [COLOR_SWATCH_IMAGE_URL] : []),
    isSwatch: true,
  },
  {
    key: "backCoverStyle",
    slug: "back-cover-style",
    title: "Back cover style",
    description:
      "For any choice using your own photo(s): Our designer will email you separately to ask for the photo file.",
    required: true,
    component: OptionGridStep,
    props: { options: BACK_COVER_STYLE_OPTIONS },
    isComplete: (d) => Boolean(d.backCoverStyle),
    getErrorMessage: () => "Please select an option to continue.",
    getSummary: (d) =>
      getOptionLabel(BACK_COVER_STYLE_OPTIONS, d.backCoverStyle),
    getImages: (d) =>
      [getOptionImage(BACK_COVER_STYLE_OPTIONS, d.backCoverStyle)].filter(
        Boolean,
      ),
  },
  {
    key: "backCoverColor",
    slug: "back-cover-color",
    title: "Select a color for your back cover",
    description:
      "We recommend choosing the same color as your front cover for a consistent look.",
    required: true,
    component: ColorPickerStep,
    props: {
      options: COVER_COLOR_PALETTE,
      swatchImageUrl: COLOR_SWATCH_IMAGE_URL,
    },
    isComplete: (d) => Boolean(d.backCoverColor),
    getErrorMessage: () => "Please select a color to continue.",
    getSummary: (d) => getOptionLabel(COVER_COLOR_PALETTE, d.backCoverColor),
    getImages: (d) => (d.backCoverColor ? [COLOR_SWATCH_IMAGE_URL] : []),
    isSwatch: true,
    getDefaultValue: (d) => d.frontCoverColor || null,
    getExtraProps: (d) => ({ matchColorId: d.frontCoverColor }),
  },
  {
    key: "messageToDesigner",
    slug: "message-to-designer",
    title: "Anything else we should know?",
    description:
      "Share any notes, preferences, or special requests for the designer laying out your book. You can leave this blank.",
    required: false,
    component: TextFieldStep,
    props: {
      label: "Message to designer",
      placeholder: "Write your questions or notes here...",
      multiline: true,
    },
    isComplete: () => true,
    getErrorMessage: () => "",
    getSummary: (d) => d.messageToDesigner?.trim() || null,
  },
];

export const FLOW_STEP_COUNT = STEPS.length + 1;

export const INITIAL_DETAILS = {
  firstName: "",
  lastName: "",
  title: "",
  subtitle: "",
  dedication: "",
  photoLayout: null,
  addOns: [],
  frontCoverStyle: null,
  frontCoverColor: null,
  backCoverStyle: null,
  backCoverColor: null,
  messageToDesigner: "",
};

const DetailsWizard = () => {
  const navigate = useNavigate();
  const { stepSlug } = useParams();
  const [searchParams] = useSearchParams();
  const fromReview = searchParams.get("from") === "review";
  const { bookDetails, updateBookDetails } = useOutletContext();
  const [hasAttemptedNext, setHasAttemptedNext] = useState(false);

  const stepIndex = STEPS.findIndex((s) => s.slug === stepSlug);
  const activeStep = stepIndex === -1 ? null : STEPS[stepIndex];

  const getInitialValue = (targetStep, targetBookDetails) => {
    if (!targetStep) return undefined;
    const current = targetStep.getValue
      ? targetStep.getValue(targetBookDetails)
      : targetBookDetails[targetStep.key];
    if (current) return current;
    return targetStep.getDefaultValue?.(targetBookDetails) ?? current;
  };

  const [draftValue, setDraftValue] = useState(() =>
    getInitialValue(activeStep, bookDetails),
  );

  useEffect(() => {
    setDraftValue(getInitialValue(activeStep, bookDetails));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep?.key]);

  if (stepIndex === -1) {
    return <Navigate to={`/book/details/${STEPS[0].slug}`} replace />;
  }

  const step = activeStep;
  const StepComponent = step.component;

  const draftBookDetails = { ...bookDetails, [step.key]: draftValue };

  const handleChange = (nextValue) => {
    setDraftValue(
      step.setValue
        ? step.setValue(draftBookDetails, nextValue)[step.key]
        : nextValue,
    );
  };

  const canAdvance = step.isComplete(draftBookDetails);
  const showStepError = hasAttemptedNext && !canAdvance;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEPS.length - 1;

  const handleBack = () => {
    setHasAttemptedNext(false);
    if (fromReview) {
      navigate("/book/review");
      return;
    }
    if (isFirstStep) {
      navigate("/book/overview");
      return;
    }
    navigate(`/book/details/${STEPS[stepIndex - 1].slug}`);
  };

  const handleNext = () => {
    if (!canAdvance) {
      setHasAttemptedNext(true);
      return;
    }
    track(EVENTS.BOOK_WIZARD_STEP_COMPLETED, {
      stepSlug: step.slug,
      stepIndex,
      isLastStep,
    });
    updateBookDetails((prev) => ({ ...prev, [step.key]: draftValue }));
    if (isLastStep || fromReview) {
      navigate("/book/review");
      return;
    }
    setHasAttemptedNext(false);
    navigate(`/book/details/${STEPS[stepIndex + 1].slug}`);
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <StepProgress step={stepIndex + 1} total={FLOW_STEP_COUNT} />
        <Heading>
          <Title1 as="h2">
            {step.title} ({step.required ? "Required" : "Optional"})
          </Title1>
          {step.description && <Description>{step.description}</Description>}
        </Heading>

        <StepComponent
          {...step.props}
          {...step.getExtraProps?.(bookDetails)}
          required={step.required}
          value={draftValue}
          onChange={handleChange}
          showError={hasAttemptedNext}
        />

        {showStepError && (
          <ErrorRow>
            <TbInfoCircleFilled size={20} />
            <Subhead>{step.getErrorMessage(draftBookDetails)}</Subhead>
          </ErrorRow>
        )}

        <ButtonRow>
          <Button variant="tertiary" onButtonClick={handleBack}>
            <TbArrowLeft /> Back
          </Button>
          <Button variant="primary" onButtonClick={handleNext}>
            {fromReview
              ? "Save & return to review"
              : isLastStep
                ? "Continue to review"
                : "Next"}{" "}
            <TbArrowRight />
          </Button>
        </ButtonRow>
      </ContentWrapper>
    </PageContainer>
  );
};

export default DetailsWizard;
